import React from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  IconButton,
  MenuItem,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import EventNoteOutlinedIcon from "@mui/icons-material/EventNoteOutlined";
import InboxOutlinedIcon from "@mui/icons-material/InboxOutlined";

const ui = {
  primary: "#009db9",
  primaryDark: "#007a8a",
  primarySoft: "#e6f6f9",
  text: "#1f2933",
  muted: "#6b7280",
  border: "#e5e7eb",
  surface: "#f7f9fb",
  success: "#2e7d32",
  successSoft: "#e8f5e9",
  danger: "#d32f2f",
  dangerSoft: "#ffebee",
  warning: "#b26a00",
  warningSoft: "#fff4e5",
  radius: "16px",
  shadow: "0 4px 20px rgba(16, 24, 40, 0.06)",
};

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "10px",
    backgroundColor: "#fff",
    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: ui.primary },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: ui.primary,
    },
    "&.Mui-disabled": { backgroundColor: "#f3f5f7" },
  },
  "& label.Mui-focused": { color: ui.primary },
};

const selectMenuProps = { PaperProps: { sx: { maxHeight: 320 } } };

const headCellSx = {
  fontWeight: 700,
  color: ui.primaryDark,
  backgroundColor: ui.primarySoft,
  fontSize: "0.8rem",
  py: 1.5,
  borderBottom: `1px solid ${ui.border}`,
};

const bodyCellSx = {
  color: ui.text,
  py: 1.75,
  fontSize: "0.875rem",
  borderBottom: `1px solid ${ui.border}`,
};

const mobileCardSx = {
  border: `1px solid ${ui.border}`,
  borderRadius: "12px",
  p: 2,
  backgroundColor: "#fff",
};

const outlineButtonSx = (color, soft) => ({
  flex: 1,
  textTransform: "none",
  fontWeight: 600,
  borderRadius: "8px",
  color,
  borderColor: color,
  "&:hover": { borderColor: color, backgroundColor: soft },
  "&.Mui-disabled": { color: "#b0b7be", borderColor: "#e0e4e8" },
});

const chipTones = {
  success: { bg: ui.successSoft, color: ui.success },
  danger: { bg: ui.dangerSoft, color: ui.danger },
  warning: { bg: ui.warningSoft, color: ui.warning },
  neutral: { bg: "#eef1f4", color: "#4b5563" },
};

const PillChip = ({ label, tone = "neutral" }) => {
  const t = chipTones[tone] || chipTones.neutral;
  return (
    <Chip
      size="small"
      label={label}
      sx={{
        fontWeight: 700,
        borderRadius: "8px",
        backgroundColor: t.bg,
        color: t.color,
      }}
    />
  );
};

const EmptyState = ({ icon: Icon = InboxOutlinedIcon, title, message }) => (
  <Box sx={{ textAlign: "center", py: { xs: 5, md: 7 }, px: 2 }}>
    <Box
      sx={{
        width: 64,
        height: 64,
        mx: "auto",
        mb: 2,
        borderRadius: "50%",
        backgroundColor: ui.primarySoft,
        color: ui.primary,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Icon sx={{ fontSize: 32 }} />
    </Box>
    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: ui.text }}>
      {title}
    </Typography>
    {message && (
      <Typography
        variant="body2"
        sx={{ color: ui.muted, mt: 0.5, maxWidth: 360, mx: "auto" }}
      >
        {message}
      </Typography>
    )}
  </Box>
);

const ListSkeleton = ({ rows = 5 }) => (
  <Box
    role="status"
    aria-label="Loading"
    sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}
  >
    {Array.from({ length: rows }).map((_, i) => (
      <Skeleton key={i} variant="rounded" height={56} animation="wave" />
    ))}
  </Box>
);

const SectionCard = ({ title, action, children }) => (
  <Card
    sx={{
      mb: { xs: 2.5, md: 4 },
      borderRadius: ui.radius,
      border: `1px solid ${ui.border}`,
      backgroundColor: "#fff",
      boxShadow: ui.shadow,
      overflow: "visible",
    }}
  >
    <CardContent
      sx={{
        p: { xs: 2, sm: 3, md: 4 },
        "&:last-child": { pb: { xs: 2, sm: 3, md: 4 } },
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          justifyContent: "space-between",
          alignItems: { xs: "stretch", sm: "center" },
          gap: 2,
          mb: 3,
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontWeight: 700,
            color: ui.text,
            pl: 1.5,
            borderLeft: `4px solid ${ui.primary}`,
            lineHeight: 1.3,
          }}
        >
          {title}
        </Typography>
        {action}
      </Box>
      {children}
    </CardContent>
  </Card>
);

const formatDate = (dateString) =>
  new Date(dateString).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });

const VacationRequestsTable = ({
  dataLoading,
  filteredData,
  totalRecords,
  filter,
  setFilter,
  page,
  rowsPerPage,
  handleChangePage,
  handleChangeRowsPerPage,
  handleOpenModal,
}) => {
  const statusTone = (status) =>
    status === "APPROVED"
      ? "success"
      : status === "DENIED"
      ? "danger"
      : "warning";

  const statusLabel = (status) =>
    status ? status.charAt(0) + status.slice(1).toLowerCase() : "N/A";

  const statusChip = (status) => (
    <PillChip label={statusLabel(status)} tone={statusTone(status)} />
  );

  const renderActions = (request, withLabels) => {
    const disabled = request.status !== "PENDING";

    if (withLabels) {
      return (
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            onClick={() => handleOpenModal(request, "Accept")}
            disabled={disabled}
            startIcon={<CheckCircleOutlineIcon fontSize="small" />}
            variant="outlined"
            size="small"
            sx={outlineButtonSx(ui.success, ui.successSoft)}
          >
            Accept
          </Button>
          <Button
            onClick={() => handleOpenModal(request, "Reject")}
            disabled={disabled}
            startIcon={<CancelOutlinedIcon fontSize="small" />}
            variant="outlined"
            size="small"
            sx={outlineButtonSx(ui.danger, ui.dangerSoft)}
          >
            Reject
          </Button>
        </Box>
      );
    }

    return (
      <Box sx={{ display: "flex", gap: 0.5 }}>
        <Tooltip
          title={
            disabled ? "Only pending requests can be accepted" : "Accept"
          }
        >
          <span>
            <IconButton
              onClick={() => handleOpenModal(request, "Accept")}
              disabled={disabled}
              size="small"
              aria-label={`Accept request from ${
                request.userName || "Unknown User"
              }`}
              sx={{
                color: ui.success,
                "&:hover": { backgroundColor: ui.successSoft },
              }}
            >
              <CheckCircleOutlineIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip
          title={
            disabled ? "Only pending requests can be rejected" : "Reject"
          }
        >
          <span>
            <IconButton
              onClick={() => handleOpenModal(request, "Reject")}
              disabled={disabled}
              size="small"
              aria-label={`Reject request from ${
                request.userName || "Unknown User"
              }`}
              sx={{
                color: ui.danger,
                "&:hover": { backgroundColor: ui.dangerSoft },
              }}
            >
              <CancelOutlinedIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Box>
    );
  };

  return (
    <SectionCard
      title="Vacation requests"
      action={
        <TextField
          select
          size="small"
          label="Filter by status"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          sx={{ ...fieldSx, minWidth: { sm: 200 } }}
          SelectProps={{ MenuProps: selectMenuProps }}
        >
          <MenuItem value="All">All Requests</MenuItem>
          <MenuItem value="Pending">Pending</MenuItem>
          <MenuItem value="Approved">Approved</MenuItem>
          <MenuItem value="Rejected">Denied</MenuItem>
        </TextField>
      }
    >
      {dataLoading ? (
        <ListSkeleton rows={rowsPerPage > 5 ? 6 : rowsPerPage} />
      ) : filteredData.length === 0 ? (
        <EmptyState
          icon={EventNoteOutlinedIcon}
          title="No vacation requests found"
          message={
            filter === "All"
              ? "Vacation requests from employees will show up here."
              : "No requests match this status. Try another filter."
          }
        />
      ) : (
        <>
          {/* Desktop table */}
          <Box
            sx={{
              display: { xs: "none", lg: "block" },
              overflowX: "auto",
              border: `1px solid ${ui.border}`,
              borderRadius: "12px",
            }}
          >
            <Table sx={{ minWidth: 900 }}>
              <TableHead>
                <TableRow>
                  {[
                    "Full name",
                    "Start date",
                    "End date",
                    "Reason",
                    "Status",
                    "Comment",
                    "Created at",
                    "Actions",
                  ].map((h) => (
                    <TableCell key={h} sx={headCellSx}>
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredData.map((request) => (
                  <TableRow
                    key={request.id}
                    hover
                    sx={{ "&:last-child td": { borderBottom: 0 } }}
                  >
                    <TableCell
                      sx={{
                        ...bodyCellSx,
                        fontWeight: 600,
                        wordBreak: "break-word",
                      }}
                    >
                      {request.userName || "Unknown User"}
                    </TableCell>
                    <TableCell sx={bodyCellSx}>
                      {formatDate(request.startDate)}
                    </TableCell>
                    <TableCell sx={bodyCellSx}>
                      {formatDate(request.endDate)}
                    </TableCell>
                    <TableCell
                      sx={{
                        ...bodyCellSx,
                        maxWidth: 200,
                        wordBreak: "break-word",
                      }}
                    >
                      {request.reason || "N/A"}
                    </TableCell>
                    <TableCell sx={bodyCellSx}>
                      {statusChip(request.status)}
                    </TableCell>
                    <TableCell
                      sx={{
                        ...bodyCellSx,
                        maxWidth: 200,
                        wordBreak: "break-word",
                      }}
                    >
                      {request.comment || "N/A"}
                    </TableCell>
                    <TableCell sx={bodyCellSx}>
                      {formatDate(request.createdAt)}
                    </TableCell>
                    <TableCell sx={bodyCellSx}>
                      {renderActions(request, false)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>

          {/* Mobile / tablet cards */}
          <Box
            sx={{
              display: { xs: "flex", lg: "none" },
              flexDirection: "column",
              gap: 1.5,
            }}
          >
            {filteredData.map((request) => (
              <Box key={request.id} sx={mobileCardSx}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    gap: 1,
                    mb: 1.5,
                  }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Typography
                      sx={{
                        fontWeight: 700,
                        color: ui.text,
                        wordBreak: "break-word",
                      }}
                    >
                      {request.userName || "Unknown User"}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: ui.muted, wordBreak: "break-word" }}
                    >
                      {request.reason || "N/A"}
                    </Typography>
                  </Box>
                  {statusChip(request.status)}
                </Box>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
                    gap: 1,
                    p: 1.5,
                    mb: 1.5,
                    borderRadius: "10px",
                    backgroundColor: ui.surface,
                  }}
                >
                  <Box>
                    <Typography variant="caption" sx={{ color: ui.muted }}>
                      Start date
                    </Typography>
                    <Typography
                      sx={{ fontWeight: 600, fontSize: "0.85rem" }}
                    >
                      {formatDate(request.startDate)}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" sx={{ color: ui.muted }}>
                      End date
                    </Typography>
                    <Typography
                      sx={{ fontWeight: 600, fontSize: "0.85rem" }}
                    >
                      {formatDate(request.endDate)}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" sx={{ color: ui.muted }}>
                      Created at
                    </Typography>
                    <Typography
                      sx={{ fontWeight: 600, fontSize: "0.85rem" }}
                    >
                      {formatDate(request.createdAt)}
                    </Typography>
                  </Box>
                  <Box sx={{ gridColumn: "1 / -1" }}>
                    <Typography variant="caption" sx={{ color: ui.muted }}>
                      Comment
                    </Typography>
                    <Typography
                      sx={{
                        fontWeight: 600,
                        fontSize: "0.85rem",
                        wordBreak: "break-word",
                      }}
                    >
                      {request.comment || "N/A"}
                    </Typography>
                  </Box>
                </Box>
                {renderActions(request, true)}
              </Box>
            ))}
          </Box>
        </>
      )}

      <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        component="div"
        count={totalRecords}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        sx={{
          mt: 2,
          color: ui.text,
          "& .MuiTablePagination-toolbar": {
            flexWrap: "wrap",
            justifyContent: { xs: "center", sm: "flex-end" },
            px: 0,
          },
          "& .MuiTablePagination-spacer": {
            display: { xs: "none", sm: "block" },
          },
        }}
      />
    </SectionCard>
  );
};

export default VacationRequestsTable;